// components/AddTypeForm.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useAuthContext } from "../context/AuthContext";
import { usePositions } from "../hooks/usePositions";
import {
  getTypeQuickReplies,
  createTypeQuickReply,
  updateTypeQuickReply,
  deleteTypeQuickReply,
  fetchData,
} from "../api/client";
import { useWorkspaceConfig } from "../context/WorkspaceConfigContext";
import { useEcommerceChannelConfigured } from "../hooks/useEcommerceChannelConfigured";
import { SUBTYPE_SCOPE_BY_LEVEL } from "../utils/typeSubtypeCascade";
import TypeKnowledgeSection, { prepareTypeKnowledgeForSave } from "./Types/TypeKnowledgeSection";
import TeamTaskTemplatesSection from "./Types/TeamTaskTemplatesSection";
import SemicolonListInput, { joinSemicolonList, parseSemicolonList } from "./Types/SemicolonListInput";

const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");
const n = (v) => (v === "" || v == null ? null : Number(v));

function additionalFieldsFromType(typeData) {
  return {
    produtos_padrao: typeData?.produtos_padrao ?? "",
    campanhas_padrao: typeData?.campanhas_padrao ?? "",
    ocultar_produto: Number(typeData?.ocultar_produto) === 1 ? 1 : 0,
    ocultar_campanha: Number(typeData?.ocultar_campanha) === 1 ? 1 : 0,
    ocultar_dataadicional: Number(typeData?.ocultar_dataadicional) === 1 ? 1 : 0,
    ocultar_textoadicional: Number(typeData?.ocultar_textoadicional) === 1 ? 1 : 0,
    publico_ecom: Number(typeData?.publico_ecom) === 1 ? 1 : 0,
    nome_ecom: typeData?.nome_ecom ?? "",
    ecom_escopo_entrega: Number(typeData?.ecom_escopo_entrega ?? 0),
    envia_pesquisa:
      typeData?.envia_pesquisa != null && Number(typeData.envia_pesquisa) > 0
        ? Number(typeData.envia_pesquisa)
        : 0,
    id_team_board:
      typeData?.id_team_board != null && Number(typeData.id_team_board) > 0
        ? Number(typeData.id_team_board)
        : null,
  };
}

const AddTypeForm = ({
  typeData,
  onClose,
  onSubmit,
  isLoading,
  entityKind = "type",
  subtypeLevel = null,
  inheritedConfig = null,
  parentLabel = "",
  rootTypeId = null,
  subtypeChainIds = {},
}) => {
  const isSubtype = entityKind === "subtype" && subtypeLevel >= 1 && subtypeLevel <= 3;
  const eavScope = isSubtype ? SUBTYPE_SCOPE_BY_LEVEL[subtypeLevel] : "tipo";
  const { userData } = useAuthContext();
  const { byKey } = useWorkspaceConfig();
  const { configured: ecommerceChannelConfigured } = useEcommerceChannelConfigured();

  // Cargos para permissão
  const { data: posData } = usePositions(1, 500, "");
  const positions = useMemo(
    () => posData?.positions || posData?.data || [],
    [posData]
  );

  const [errorMessage, setErrorMessage] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [quickRepliesLoading, setQuickRepliesLoading] = useState(false);
  const [quickRepliesError, setQuickRepliesError] = useState(null);
  const [quickReplyDialogOpen, setQuickReplyDialogOpen] = useState(false);
  const [editingQuickReply, setEditingQuickReply] = useState(null);
  const [quickReplyMensagem, setQuickReplyMensagem] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [typeEavFields, setTypeEavFields] = useState([]);
  const [typeEavLoading, setTypeEavLoading] = useState(false);
  const [knowledgeSaving, setKnowledgeSaving] = useState(false);
  const knowledgeRef = useRef(null);
  const [publishedSurveys, setPublishedSurveys] = useState([]);
  const [loadingSurveys, setLoadingSurveys] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingSurveys(true);
    fetchData("/surveys?page=1&limit=100&status=published", "GET")
      .then((res) => {
        if (cancelled) return;
        const list = res?.surveys || res?.data || res?.items || [];
        setPublishedSurveys(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setPublishedSurveys([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSurveys(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const labelProduto = (byKey("nomeproduto") || "Produto").trim() || "Produto";
  const labelCampanha = (byKey("nomecampanha") || "Campanha").trim() || "Campanha";
  const labelDataAdicional =
    (byKey("dataadicionallead") || "Data adicional").trim() || "Data adicional";
  const labelTextoAdicional =
    (byKey("textoadicionallead") || "Texto adicional").trim() || "Texto adicional";

  const [formData, setFormData] = useState({
    ...typeData,
    ...additionalFieldsFromType(typeData),
    // status vem como 0/1
    status: typeData?.status == null ? 1 : Number(typeData.status),
    ordem: typeData?.ordem ?? "",
    id_usuario: typeData?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    allowed_positions: Array.isArray(typeData?.allowed_positions)
      ? typeData.allowed_positions
      : [],

    // ✅ campos pedidos
    // tipo (0 = Comercial, 1 = Relacionamento) — usa o que veio do banco
    tipo: typeData?.tipo == null ? 0 : Number(typeData.tipo),
    // default (0/1)
    default: typeData?.default == null ? 0 : Number(typeData.default),
  });

  // Quando trocar de tipo (edição), recarregar dados de forma segura
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...typeData,
      ...additionalFieldsFromType(typeData),
      status: typeData?.status == null ? 1 : Number(typeData.status),
      ordem: typeData?.ordem ?? "",
      id_usuario: typeData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      allowed_positions: Array.isArray(typeData?.allowed_positions)
        ? typeData.allowed_positions
        : [],
      // ✅ garantir que o select reflita o valor do banco
      tipo: typeData?.tipo == null ? 0 : Number(typeData.tipo),
      default: typeData?.default == null ? 0 : Number(typeData.default),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeData?.id]);

  const typeId = formData.id || typeData?.id;

  useEffect(() => {
    if (!typeId) {
      setTypeEavFields([]);
      return;
    }
    let cancelled = false;
    setTypeEavLoading(true);
    fetchData(
      `/custom-fields/definitions?entityType=lead&scope=${encodeURIComponent(eavScope)}&scopeId=${encodeURIComponent(typeId)}`,
      "GET"
    )
      .then((res) => {
        if (!cancelled) {
          setTypeEavFields(res?.data?.items || res?.items || []);
        }
      })
      .catch(() => {
        if (!cancelled) setTypeEavFields([]);
      })
      .finally(() => {
        if (!cancelled) setTypeEavLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [typeId, eavScope]);

  useEffect(() => {
    if (!typeId) {
      setQuickReplies([]);
      return;
    }
    let cancelled = false;
    setQuickRepliesLoading(true);
    setQuickRepliesError(null);
    getTypeQuickReplies(typeId)
      .then(({ items }) => {
        if (!cancelled) setQuickReplies(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setQuickRepliesError(err?.message || "Erro ao carregar respostas.");
          setQuickReplies([]);
        }
      })
      .finally(() => {
        if (!cancelled) setQuickRepliesLoading(false);
      });
    return () => { cancelled = true; };
  }, [typeId]);

  const openAddQuickReply = () => {
    setEditingQuickReply(null);
    setQuickReplyMensagem("");
    setQuickReplyDialogOpen(true);
  };

  const openEditQuickReply = (item) => {
    setEditingQuickReply(item);
    setQuickReplyMensagem(item.mensagem || "");
    setQuickReplyDialogOpen(true);
  };

  const closeQuickReplyDialog = () => {
    setQuickReplyDialogOpen(false);
    setEditingQuickReply(null);
    setQuickReplyMensagem("");
  };

  const saveQuickReply = async () => {
    const msg = (quickReplyMensagem || "").trim();
    if (!msg) return;
    if (!typeId) return;
    setQuickRepliesError(null);
    try {
      if (editingQuickReply?.id) {
        await updateTypeQuickReply(typeId, editingQuickReply.id, { mensagem: msg });
        setQuickReplies((prev) =>
          prev.map((r) => (r.id === editingQuickReply.id ? { ...r, mensagem: msg } : r))
        );
      } else {
        const created = await createTypeQuickReply(typeId, { mensagem: msg });
        setQuickReplies((prev) => [...prev, { id: created?.id, mensagem: msg, id_tipolead: typeId }]);
      }
      closeQuickReplyDialog();
    } catch (err) {
      setQuickRepliesError(err?.response?.data?.message || err?.message || "Erro ao salvar.");
    }
  };

  const handleDeleteQuickReply = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDeleteQuickReply = async () => {
    if (!deleteConfirmId || !typeId) return;
    try {
      await deleteTypeQuickReply(typeId, deleteConfirmId);
      setQuickReplies((prev) => prev.filter((r) => r.id !== deleteConfirmId));
      setDeleteConfirmId(null);
    } catch (err) {
      setQuickRepliesError(err?.response?.data?.message || err?.message || "Erro ao excluir.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // converter 0/1 quando necessário
    if (["status", "tipo", "default"].includes(name)) {
      setFormData((p) => ({ ...p, [name]: Number(value) }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    const payload = {
      id: formData.id,
      nome: (formData.nome || "").trim(),
      descricao: formData.descricao || "",
      ordem: n(digitsOnly(formData.ordem)),
      status: Number(formData.status) === 1 ? 1 : 0,
      id_usuario: formData.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,

      default: Number(formData.default) === 1 ? 1 : 0, // 0=Não default, 1=Default

      // cargos permitidos
      allowed_positions: Array.isArray(formData.allowed_positions)
        ? formData.allowed_positions
        : [],

      produtos_padrao: joinSemicolonList(parseSemicolonList(formData.produtos_padrao)) || null,
      campanhas_padrao: joinSemicolonList(parseSemicolonList(formData.campanhas_padrao)) || null,
      ocultar_produto: Number(formData.ocultar_produto) === 1 ? 1 : 0,
      ocultar_campanha: Number(formData.ocultar_campanha) === 1 ? 1 : 0,
      ocultar_dataadicional: Number(formData.ocultar_dataadicional) === 1 ? 1 : 0,
      ocultar_textoadicional: Number(formData.ocultar_textoadicional) === 1 ? 1 : 0,
      publico_ecom: Number(formData.publico_ecom) === 1 ? 1 : 0,
      nome_ecom: (formData.nome_ecom || "").trim() || null,
      ecom_escopo_entrega: Number(formData.publico_ecom) === 1
        ? Number(formData.ecom_escopo_entrega ?? 0)
        : 0,
      envia_pesquisa:
        formData.envia_pesquisa != null && Number(formData.envia_pesquisa) > 0
          ? Number(formData.envia_pesquisa)
          : 0,
      id_team_board:
        formData.id_team_board != null && Number(formData.id_team_board) > 0
          ? Number(formData.id_team_board)
          : null,
    };

    if (!isSubtype) {
      payload.tipo = Number(formData.tipo) === 1 ? 1 : 0;
    }

    try {
      setKnowledgeSaving(true);
      const knowledgePayload = await prepareTypeKnowledgeForSave(knowledgeRef);
      payload.source_urls = knowledgePayload.source_urls;
      payload.documents = knowledgePayload.documents;
      setKnowledgeSaving(false);
      await onSubmit(payload);
    } catch (error) {
      setKnowledgeSaving(false);
      setErrorMessage(
        error?.response?.data?.message || "Erro ao salvar o tipo."
      );
    }
  };

  // opções do Autocomplete
  const positionOptions = positions.map((p) => ({
    id: p.id,
    label: p.nome || p.name || `#${p.id}`,
  }));
  const valuePositions = positionOptions.filter((opt) =>
    (formData.allowed_positions || []).includes(opt.id)
  );

  const formTitle = isSubtype
    ? typeData?.id
      ? `Editar Subtipo ${subtypeLevel}`
      : `Adicionar Subtipo ${subtypeLevel}`
    : typeData?.id
    ? "Editar Tipo"
    : "Adicionar Tipo";

  const inheritHint = (fieldLabel, inheritedValue) => {
    if (!isSubtype || !inheritedConfig) return null;
    const hasValue =
      inheritedValue != null &&
      String(inheritedValue).trim() !== "" &&
      !(Array.isArray(inheritedValue) && inheritedValue.length === 0);
    if (!hasValue) return null;
    const from = parentLabel ? ` (${parentLabel})` : "";
    return `Herdado${from}: ${Array.isArray(inheritedValue) ? `${inheritedValue.length} cargo(s)` : inheritedValue}`;
  };

  const showEcomPipelineFields =
    ecommerceChannelConfigured && isSubtype && subtypeLevel >= 1 && subtypeLevel <= 3;

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {formTitle}
      </Typography>
      {isSubtype && parentLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configurações não definidas neste subtipo herdam do nível superior ({parentLabel}).
          Subtipos mais específicos sobrescrevem os anteriores na plataforma.
        </Typography>
      )}

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Nome"
              name="nome"
              value={formData.nome || ""}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Ordem"
              name="ordem"
              value={formData.ordem ?? ""}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  ordem: digitsOnly(e.target.value),
                }))
              }
              inputMode="numeric"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              name="descricao"
              value={formData.descricao || ""}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          {!isSubtype && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="tipo-label">Tipo de atendimento</InputLabel>
                <Select
                  labelId="tipo-label"
                  label="Tipo de atendimento"
                  name="tipo"
                  value={Number(formData.tipo) || 0}
                  onChange={handleChange}
                >
                  <MenuItem value={0}>Comercial</MenuItem>
                  <MenuItem value={1}>Relacionamento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ✅ Default (0/1) */}
          <Grid item xs={12} md={6} alignItems="center" display="flex">
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.default) === 1}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      default: e.target.checked ? 1 : 0,
                    }))
                  }
                />
              }
              label={
                Number(formData.default) === 1
                  ? isSubtype
                    ? "Subtipo default"
                    : "Tipo default"
                  : "Não é default"
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Enviar pesquisa ao concluir"
              value={formData.envia_pesquisa ?? 0}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  envia_pesquisa:
                    e.target.value === "" || e.target.value === "0"
                      ? 0
                      : Number(e.target.value),
                }))
              }
              helperText={
                isSubtype
                  ? "Se definido, tem prioridade sobre o tipo pai e o canal. Deixe vazio para herdar."
                  : "Se definido, tem prioridade sobre o canal. Subtipos mais específicos sobrescrevem."
              }
            >
              <MenuItem value={0}>Não enviar / herdar</MenuItem>
              {loadingSurveys && (
                <MenuItem value={formData.envia_pesquisa || 0} disabled>
                  Carregando pesquisas...
                </MenuItem>
              )}
              {!loadingSurveys &&
                publishedSurveys.map((s) => (
                  <MenuItem key={s.id} value={Number(s.id)}>
                    {s.title || s.titulo || s.nome || `#${s.id}`}
                    {(s.metricType || s.metric_type)
                      ? ` (${String(s.metricType || s.metric_type).toUpperCase()})`
                      : ""}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          {/* Cargos permitidos */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={positionOptions}
              value={valuePositions}
              onChange={(_, newValue) =>
                setFormData((p) => ({
                  ...p,
                  allowed_positions: newValue.map((v) => v.id),
                }))
              }
              disableCloseOnSelect
              getOptionLabel={(opt) => opt.label}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cargos com permissão"
                  placeholder="Selecione cargos"
                  helperText={
                    inheritHint(
                      "cargos",
                      inheritedConfig?.allowed_positions?.length
                        ? inheritedConfig.allowed_positions
                        : null
                    ) ||
                    (isSubtype
                      ? "Vazio = herda cargos do nível superior. Preencha para restringir só a este subtipo."
                      : undefined)
                  }
                />
              )}
            />
          </Grid>

          {/* Campos adicionais da solicitação (legado xpro + listas por tipo) */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Campos adicionais da solicitação
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Os rótulos (ex.: Segmento, Trechos) ficam em{" "}
              <RouterLink to="/settings/custom-fields">Configurações → Campos adicionais → Labels (legado)</RouterLink>
              . Aqui você define se {isSubtype ? "este subtipo" : "este tipo"} exibe cada campo e as listas padrão de sugestão.
              {isSubtype ? " Valores em branco ou padrão herdam do tipo pai." : ""}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(formData.ocultar_produto) !== 1}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          ocultar_produto: e.target.checked ? 0 : 1,
                        }))
                      }
                    />
                  }
                  label={`Exibir ${labelProduto}`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(formData.ocultar_campanha) !== 1}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          ocultar_campanha: e.target.checked ? 0 : 1,
                        }))
                      }
                    />
                  }
                  label={`Exibir ${labelCampanha}`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(formData.ocultar_dataadicional) !== 1}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          ocultar_dataadicional: e.target.checked ? 0 : 1,
                        }))
                      }
                    />
                  }
                  label={`Exibir ${labelDataAdicional}`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(formData.ocultar_textoadicional) !== 1}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          ocultar_textoadicional: e.target.checked ? 0 : 1,
                        }))
                      }
                    />
                  }
                  label={`Exibir ${labelTextoAdicional}`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SemicolonListInput
                  label={`Lista padrão — ${labelProduto}`}
                  value={formData.produtos_padrao || ""}
                  onChange={(next) =>
                    setFormData((p) => ({ ...p, produtos_padrao: next }))
                  }
                  placeholder="Digite e pressione Enter"
                  helperText={
                    inheritHint("produtos", inheritedConfig?.produtos_padrao) ||
                    `Digite e pressione Enter (ou ;). Remova pelo × no chip. Sobrescreve a lista global do workspace para este ${isSubtype ? "subtipo" : "tipo"}.`
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SemicolonListInput
                  label={`Lista padrão — ${labelCampanha}`}
                  value={formData.campanhas_padrao || ""}
                  onChange={(next) =>
                    setFormData((p) => ({ ...p, campanhas_padrao: next }))
                  }
                  placeholder="Digite e pressione Enter"
                  helperText={
                    inheritHint("campanhas", inheritedConfig?.campanhas_padrao) ||
                    "Digite e pressione Enter (ou ;). Remova pelo × no chip."
                  }
                />
              </Grid>
            </Grid>
            {showEcomPipelineFields ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  E-commerce (balcão / loja online)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Marque etapas visíveis ao cliente em qualquer nível de subtipo (1, 2 ou 3).
                  O e-commerce exibe apenas as etapas públicas, na ordem do pipeline.
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Number(formData.publico_ecom) === 1}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              publico_ecom: e.target.checked ? 1 : 0,
                              nome_ecom: e.target.checked ? p.nome_ecom : "",
                              ecom_escopo_entrega: e.target.checked ? p.ecom_escopo_entrega : 0,
                            }))
                          }
                        />
                      }
                      label="Visível no e-commerce"
                    />
                  </Grid>
                  {Number(formData.publico_ecom) === 1 ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Nome para o cliente"
                          fullWidth
                          value={formData.nome_ecom || ""}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, nome_ecom: e.target.value }))
                          }
                          placeholder={formData.nome || "Usa o nome interno se vazio"}
                          helperText="Opcional. Ex.: exibir “Pagamento aprovado” em vez do nome interno."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel id="ecom-escopo-entrega-label">
                            Método de entrega
                          </InputLabel>
                          <Select
                            labelId="ecom-escopo-entrega-label"
                            label="Método de entrega"
                            value={Number(formData.ecom_escopo_entrega ?? 0)}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                ecom_escopo_entrega: Number(e.target.value),
                              }))
                            }
                          >
                            <MenuItem value={0}>Todos (entrega e retirada)</MenuItem>
                            <MenuItem value={1}>Somente entrega</MenuItem>
                            <MenuItem value={2}>Somente retirada (balcão)</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                          Etapas finais como “A caminho” ou “Pronto para retirada” podem ser
                          exclusivas de um fluxo.
                        </Typography>
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </>
            ) : null}
            {typeId ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Campos personalizados (novos)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Campos extras além dos slots legado, gravados na tabela EAV.
                </Typography>
                {typeEavLoading ? (
                  <CircularProgress size={22} />
                ) : typeEavFields.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum campo personalizado vinculado a este {isSubtype ? "subtipo" : "tipo"}.
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ mb: 1 }}>
                    {typeEavFields.map((f) => (
                      <ListItem key={f.id} disablePadding>
                        <ListItemText
                          primary={`${f.label} (${f.fieldKey})`}
                          secondary={f.fieldType}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to="/settings/custom-fields"
                  state={{
                    tab: 1,
                    presetScope: eavScope,
                    presetScopeId: typeId,
                    returnTo: "/settings/types",
                    returnTypeId: typeId,
                    returnTypeName: (formData.nome || typeData?.nome || "").trim(),
                    returnTypeSnapshot: {
                      ...formData,
                      id: typeId,
                      _editKind: entityKind,
                      _subtypeLevel: subtypeLevel,
                      _rootTypeId: rootTypeId,
                      _sub1Id: subtypeChainIds.sub1Id,
                      _sub2Id: subtypeChainIds.sub2Id,
                    },
                  }}
                >
                  Gerenciar campos personalizados
                </Button>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                Salve {isSubtype ? "o subtipo" : "o tipo"} uma vez para vincular campos personalizados (EAV).
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <TypeKnowledgeSection
              ref={knowledgeRef}
              sourceUrls={typeData?.source_urls || formData.source_urls || []}
              documents={typeData?.documents || formData.documents || []}
              inheritedUrls={inheritedConfig?.source_urls || []}
              inheritedDocCount={(inheritedConfig?.documents || []).length}
              parentLabel={parentLabel}
            />
          </Grid>

          {/* Respostas pré-definidas (apenas ao editar tipo existente) */}
          {typeId && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Respostas pré-definidas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Mensagens rápidas exibidas no chat para este {isSubtype ? "subtipo" : "tipo"} de lead.
                {isSubtype
                  ? " Se não houver respostas aqui, o chat usa as do nível superior (cascata)."
                  : ""}
              </Typography>
              {quickRepliesError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setQuickRepliesError(null)}>
                  {quickRepliesError}
                </Alert>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={openAddQuickReply}
                >
                  Adicionar resposta
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 280, overflow: "auto" }}>
                {quickRepliesLoading ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : quickReplies.length === 0 ? (
                  <Box sx={{ p: 2, color: "text.secondary", fontSize: "0.875rem" }}>
                    Nenhuma resposta pré-definida. Clique em &quot;Adicionar resposta&quot; para incluir.
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {quickReplies.map((item) => (
                      <ListItem
                        key={item.id}
                        secondaryAction={
                          <Box component="span">
                            <IconButton
                              size="small"
                              aria-label="Editar"
                              onClick={() => openEditQuickReply(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Excluir"
                              onClick={() => handleDeleteQuickReply(item.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={item.mensagem || "(sem texto)"}
                          primaryTypographyProps={{ variant: "body2", sx: { whiteSpace: "pre-wrap", wordBreak: "break-word" } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
              {/* Diálogo de confirmação de exclusão */}
              <Dialog open={Boolean(deleteConfirmId)} onClose={() => setDeleteConfirmId(null)}>
                <DialogTitle>Excluir resposta pré-definida?</DialogTitle>
                <DialogContent>
                  <Typography variant="body2">Esta ação não pode ser desfeita.</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDeleteConfirmId(null)}>Cancelar</Button>
                  <Button color="error" variant="contained" onClick={confirmDeleteQuickReply}>
                    Excluir
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>
          )}

          {typeId && !isSubtype ? (
            <Grid item xs={12}>
              <TeamTaskTemplatesSection
                tipoId={typeId}
                boardId={formData.id_team_board}
                onBoardIdChange={(nextId) =>
                  setFormData((prev) => ({
                    ...prev,
                    id_team_board:
                      nextId != null && Number(nextId) > 0 ? Number(nextId) : null,
                  }))
                }
              />
            </Grid>
          ) : null}
        </Grid>

        {/* Diálogo Adicionar/Editar resposta pré-definida */}
        <Dialog open={quickReplyDialogOpen} onClose={closeQuickReplyDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingQuickReply ? "Editar resposta pré-definida" : "Adicionar resposta pré-definida"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Mensagem"
              fullWidth
              multiline
              minRows={3}
              value={quickReplyMensagem}
              onChange={(e) => setQuickReplyMensagem(e.target.value)}
              placeholder="Texto que será exibido como sugestão no chat"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeQuickReplyDialog}>Cancelar</Button>
            <Button onClick={saveQuickReply} variant="contained" disabled={!(quickReplyMensagem || "").trim()}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Ativo/Inativo */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Box display="flex" justifyContent="flex-end">
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.status) === 1}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))
                  }
                />
              }
              label={Number(formData.status) === 1 ? "Ativo" : "Inativo"}
            />
          </Box>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || knowledgeSaving}>
            {isLoading || knowledgeSaving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Salvar"
            )}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddTypeForm;
